import { useState, useEffect } from 'react'
import {
  Box,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  useColorModeValue,
  HStack,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import RubiksCube from '../components/RubiksCube'
import { CubeNotation } from '../types'
import { PageMeta } from '../components/PageMeta'

const MovesNotation = () => {
  const { t } = useTranslation()
  const [currentMove, setCurrentMove] = useState<CubeNotation | undefined>()
  const bgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')

  // Handle keyboard input
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      const key = event.key.toUpperCase()
      const validMoves: Record<string, CubeNotation> = {
        'U': 'U',
        'D': 'D',
        'R': 'R',
        'L': 'L',
        'F': 'F',
        'B': 'B',
        'M': 'M',
        'E': 'E',
        'S': 'S',
      }

      // For M, E, S, we need to check both uppercase and lowercase
      const isMiddleMove = ['M', 'E', 'S'].includes(key)
      const isShiftPressed = event.shiftKey

      if (key in validMoves) {
        if (isMiddleMove) {
          // For middle moves, shift makes it counter-clockwise
          setCurrentMove(isShiftPressed ? `${key}'` as CubeNotation : key as CubeNotation)
        } else {
          // For regular moves, shift makes it counter-clockwise
          setCurrentMove(isShiftPressed ? `${key}'` as CubeNotation : validMoves[key])
        }
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [])

  // Reset current move when animation completes
  const handleMoveComplete = () => {
    setCurrentMove(undefined)
  }

  // Handle card click
  const handleMoveClick = (notation: string) => {
    setCurrentMove(notation as CubeNotation)
  }

  return (
    <Box py={8}>
      <PageMeta title="moveNotation.title" description="moveNotation.subtitle" />
      <VStack spacing={8} align="stretch">
        <Box>
          <Heading size="xl" mb={4}>{t('moveNotation.title')}</Heading>
          <Text fontSize="lg" mb={4}>
            {t('moveNotation.subtitle')}
          </Text>
          <Text fontSize="md" color="gray.600">
            Press any move key (U, D, R, L, F, B) to see the move.
            Hold Shift + key for counter-clockwise moves.
            Or click on any move card below.
          </Text>
        </Box>

        <Box
          bg={bgColor}
          p={6}
          borderRadius="xl"
          boxShadow="xl"
          aspectRatio={1}
          maxW={{ base: "100%", sm: "400px", md: "500px", lg: "600px" }}
          mx="auto"
          width="100%"
        >
          <RubiksCube
            currentMove={currentMove}
            onMoveComplete={handleMoveComplete}
          />
        </Box>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
          {Object.entries(t('moves', { returnObjects: true })).map(([notation, info]: [string, any]) => (
            <Card 
              key={notation}
              cursor="pointer"
              borderWidth="1px"
              borderColor={borderColor}
              _hover={{ transform: 'translateY(-2px)', transition: '0.2s' }}
              onClick={() => handleMoveClick(notation)}
            >
              <CardHeader pb={2}>
                <HStack justify="space-between" align="center">
                  <Text
                    fontSize="2xl"
                    fontFamily="monospace"
                    fontWeight="bold"
                    color="rubiks.red"
                  >
                    {notation}
                  </Text>
                  <Text
                    fontSize="lg"
                    fontWeight="medium"
                    color="text.default"
                  >
                    {info.name}
                  </Text>
                </HStack>
              </CardHeader>
              <CardBody pt={2}>
                <Text color="text.muted">{info.description}</Text>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      </VStack>
    </Box>
  )
}

export default MovesNotation 